"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useExperienciaProduto } from "@/components/ExperienciaProvider";
import { menuOcultaConfiguracoes } from "@/lib/followzap-navigation";
import {
  ArrowLeftRight,
  BarChart3,
  CalendarClock,
  Columns3,
  Download,
  Folder,
  Home,
  PlayCircle,
  Settings,
  Shield,
  Users,
} from "lucide-react";

const ITENS_GESTOR: Array<{ href: string; label: string; Icon: typeof Home; produtos?: readonly ["FOLLOW_PRO"] }> = [
  { href: "/gestor/inicio", label: "Inicio", Icon: Home },
  { href: "/gestor/equipe", label: "Equipe", Icon: BarChart3 },
  { href: "/gestor/pipeline", label: "Pipeline", Icon: Columns3 },
  { href: "/gestor", label: "Usuarios", Icon: Users },
  { href: "/gestor/follow-up", label: "Follow-up", Icon: CalendarClock },
  { href: "/gestor/solicitar-migracao", label: "Migrações", Icon: ArrowLeftRight },
  { href: "/gestor/baixar-clientes", label: "Baixar clientes", Icon: Download },
  { href: "/gestor/aprenda", label: "Aprenda", Icon: PlayCircle },
  { href: "/gestor/configuracoes", label: "Configuracoes", Icon: Settings },
  { href: "/drive", label: "Driver", Icon: Folder, produtos: ["FOLLOW_PRO"] },
];

const DICAS = [
  "Gestão forte começa em acompanhar o que precisa acontecer hoje, não só no fim da semana.",
  "Equipe bem orientada responde melhor quando a prioridade do dia está clara para todos.",
  "Bom gestor não espera o atraso crescer. Corrige cedo e evita acumulação desnecessária.",
  "Cobrar com contexto funciona melhor que cobrar no escuro.",
  "Ritmo diário da equipe vale mais que pico isolado de resultado.",
  "Quem acompanha de perto percebe gargalos antes que eles virem problema grande.",
  "Gestão eficiente nasce de rotina simples: ver, priorizar, agir e revisar.",
  "Meta da equipe melhora quando cada consultor entende seu próximo passo.",
  "Atraso ignorado hoje costuma virar perda de resultado amanhã.",
  "Uma equipe organizada precisa de clareza, constância e retorno rápido.",
  "Gestor bom não centraliza tudo. Ele direciona, acompanha e destrava.",
  "Notificação certa não é pressão vazia. É sinal claro de onde agir agora.",
  "Quem acompanha hoje com critério cobra menos no fim do dia.",
  "Prioridade boa reduz ruído e aumenta execução.",
  "Gestão de equipe não é só ver número. É entender o que travou a entrega.",
  "Consultor com acompanhamento frequente tende a responder com mais constância.",
  "O melhor ajuste de rota é o que acontece antes do problema ficar visível para todos.",
  "Equipe com foco no dia trabalha com mais previsibilidade.",
  "Olhar hoje, atrasados e concluídos ao mesmo tempo ajuda a decidir melhor.",
  "Gestor que revisa a fila certa protege o ritmo da equipe.",
  "Cobrar no momento certo aumenta resposta e reduz desgaste.",
  "Uma boa liderança transforma indicador em ação prática.",
  "Se o ritmo caiu, a pergunta certa vale mais que cobrança repetida.",
  "Resultados consistentes pedem acompanhamento consistente.",
  "Equipe forte é equipe que sabe o que precisa fazer agora.",
  "Gestão diária evita surpresa no fechamento da semana.",
  "A melhor cobrança é objetiva, curta e ligada ao que precisa ser resolvido.",
  "Quem acompanha carteira e agendamentos com frequência perde menos oportunidade.",
  "Liderar bem também é remover fricção para a equipe produzir melhor.",
  "Pequenos alinhamentos diários evitam grandes correções depois.",
  "Quando o gestor dá visibilidade, a equipe ganha direção.",
  "Boa gestão cria ritmo antes de criar pressa.",
  "Acompanhamento de verdade não é vigiar. É ajudar a equipe a manter o fluxo.",
  "Quem lê os sinais cedo toma decisões mais leves e mais eficazes.",
  "Equipe que recebe retorno claro evolui mais rápido.",
  "Uma carteira bem distribuída melhora a resposta do time.",
  "O gestor precisa ver tendência, não só foto do momento.",
  "Toda rotina forte de equipe nasce de repetição com critério.",
  "Direção clara reduz retrabalho e aumenta entrega.",
  "Gestão boa é aquela que antecipa, organiza e sustenta o ritmo da equipe.",
];

function dicaDoDia() {
  const dia = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "numeric",
  }).format(new Date());
  return DICAS[(Number(dia) || 1) % DICAS.length];
}

export function GestorDesktopSidebar({ pathname, integrado = false }: { pathname: string; integrado?: boolean }) {
  const { dados, experiencia, carregado: experienciaCarregada, experienciaConfirmada } = useExperienciaProduto();
  const [dicaAtual, setDicaAtual] = useState(dicaDoDia);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setDicaAtual((atual) => {
        const indiceAtual = Math.max(0, DICAS.indexOf(atual));
        return DICAS[(indiceAtual + 1) % DICAS.length];
      });
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  const itemAtivo = (href: string) => (href === "/gestor" ? pathname === href : pathname.startsWith(href));
  const itensVisiveis = ITENS_GESTOR.filter(({ href, produtos }) =>
    (!produtos || (experienciaCarregada && experienciaConfirmada && produtos.includes(experiencia.produto as "FOLLOW_PRO"))) &&
    (href !== "/gestor/configuracoes" || !menuOcultaConfiguracoes(experiencia.produto, dados?.papel)),
  );

  return (
    <aside className={`hidden lg:block lg:flex-shrink-0 ${integrado ? "lg:w-[185px]" : "lg:w-[203px]"}`}>
      <div
        data-gestor-desktop-sidebar="true"
        className={`sticky flex flex-col bg-[var(--surface-sidebar)] px-4 py-5 ${integrado ? "top-0 h-dvh border-0 shadow-none" : "top-8 h-[calc(100dvh-4rem)] rounded-[24px] border border-[var(--border)] shadow-[var(--shadow-soft)]"}`}
      >
        <div>
          <div className={`flex items-center gap-2 pb-4 ${integrado ? "" : "border-b border-[var(--border-subtle)]"}`}>
            <div
              data-gestor-desktop-brand="true"
              className="flex h-11 w-11 items-center justify-center rounded-[14px] text-sm font-bold text-white"
            >
              BP
            </div>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-[var(--text-primary)]">Acompanhamento</p>
              <p className="text-[11px] text-[var(--text-soft)]">Gestao</p>
            </div>
          </div>

          <nav className="mt-5 space-y-2">
            {itensVisiveis.map(({ href, label, Icon }) => (
              <Link
                key={href}
                href={href}
                prefetch
                className={`flex h-9 w-full items-center gap-2 rounded-[12px] px-2.5 text-[12.5px] font-medium transition-colors ${
                  itemAtivo(href) ? "bg-[var(--surface-active)] text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:bg-[var(--surface-card-2)]"
                }`}
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[9px] bg-[var(--surface-card-2)]">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 truncate">{label}</span>
              </Link>
            ))}
          </nav>
        </div>

        <div className="mt-auto space-y-4">
          <div className="rounded-[18px] border border-[var(--border)] bg-[var(--surface-card-2)] px-3 py-4 text-center">
            <div
              data-gestor-desktop-tip-icon="true"
              className="mx-auto flex h-8 w-8 items-center justify-center rounded-[10px]"
            >
              <Shield className="h-4 w-4" />
            </div>
            <p className="mt-2.5 text-[14px] font-semibold leading-tight text-[var(--text-primary)]">Dica Bem Protege</p>
            <p className="mt-2 line-clamp-4 text-[11px] leading-5 text-[var(--text-secondary)]">{dicaAtual}</p>
          </div>
        </div>
      </div>
    </aside>
  );
}
